// Deterministic in-memory stub scanner adapter.
//
// This is the DEFAULT adapter shipped with Echo Engine so third-party
// developers can build gameplay with zero backend. It contains NO camera APIs,
// NO Firebase, NO network requests, NO API routes, NO Web Workers, NO WGSL
// compute, and NO external domains. State lives in a module-level Map that
// survives for the current browser session only (no localStorage / cookies /
// IndexedDB) and resets on full reload.
//
// Replacement seam: implement StickerScannerAdapter elsewhere (e.g. a private
// cloud adapter) and pass it to createStickerService(). Nothing here is part of
// the public gameplay import surface.

import type {
  AdapterScanRequest,
  AdapterScanSession,
  StickerScannerAdapter,
} from '../sticker_adapter';
import type {
  JsonObject,
  ProjectTag,
  StickerCode,
  StickerCodeStatus,
} from '../sticker_types';
import { StickerError, UNASSIGNED_TAG } from '../sticker_types';
import { deepCloneJson } from './clone';

// Fixture codes a developer can cycle through. Opaque decimal strings.
const FIXTURE_CODES: StickerCode[] = [
  '1000000001',
  '1000000002',
  '1000000003',
  '9999999999', // fault fixture: scanning this simulates a hardware failure
];

const FAULT_CODE = '9999999999';

// Two codes intentionally share one underlying save to exercise linkedCodes.
const LINK_GROUPS: StickerCode[][] = [['1000000001', '1000000002']];

// Project tags per fixture code, kept coherent with the IndexedDB fixtures.
// Codes without an entry resolve to the Unassigned default.
const TAG_BY_CODE: Record<StickerCode, ProjectTag> = {
  '1000000001': { id: 'series-alpha', name: 'Series Alpha' },
  '1000000002': { id: 'series-alpha', name: 'Series Alpha' },
  '1000000003': { id: 'series-beta', name: 'Series Beta' },
};

function tagForCode(code: StickerCode): ProjectTag {
  return { ...(TAG_BY_CODE[code] ?? UNASSIGNED_TAG) };
}

interface StoredSession {
  sessionId: string;
  code: StickerCode;
  linkedCodes: StickerCode[];
  tag: ProjectTag;
  save: JsonObject;
  isNewCode: boolean;
}

// Persists across service instances for the browser session, not across reload.
const sessionStore = new Map<string, StoredSession>();
// Persist save per link-group key so re-scanning a linked code restores data.
const saveStore = new Map<string, JsonObject>();
const codeToKey = new Map<StickerCode, string>();
const usedCodes = new Set<StickerCode>(LINK_GROUPS.flat());

for (const group of LINK_GROUPS) {
  const key = group.join('+');
  for (const code of group) codeToKey.set(code, key);
}

let cycleIndex = 0;
let sessionCounter = 0;

function linkGroupKey(code: StickerCode): string {
  const assigned = codeToKey.get(code);
  if (assigned) return assigned;
  for (const group of LINK_GROUPS) {
    if (group.includes(code)) return group.join('+');
  }
  return code;
}

function saveIdForKey(key: string): string {
  return `save-${key}`;
}

function linkedCodesFor(code: StickerCode): StickerCode[] {
  const key = linkGroupKey(code);
  return [...codeToKey.entries()]
    .filter(([other, otherKey]) => other !== code && otherKey === key)
    .map(([other]) => other);
}

function membersFor(code: StickerCode) {
  return [code, ...linkedCodesFor(code)].map((memberCode) => ({
    code: memberCode,
    tag: tagForCode(memberCode),
  }));
}

function seedSave(_code: StickerCode): JsonObject {
  return {
    playerName: 'Guest',
    score: 0,
    tokens: [],
    level: 1,
    checkpoint: 'start',
  };
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new StickerError('cancelled', 'Scan cancelled'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new StickerError('cancelled', 'Scan cancelled'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export class StubScannerAdapter implements StickerScannerAdapter {
  readonly id = 'stub';

  async scan(request: AdapterScanRequest): Promise<AdapterScanSession> {
    // Simulate the time it takes to bring up a scan flow.
    await delay(400, request.signal);

    const code =
      request.preferredCode ?? FIXTURE_CODES[cycleIndex++ % FIXTURE_CODES.length];

    if (code === FAULT_CODE) {
      throw new StickerError('scan_failed', 'Simulated scan failure');
    }
    if (request.excludedCode === code) {
      throw new StickerError(
        'same_sticker',
        'Scan a different robot that is already in the collection.'
      );
    }

    const isNewCode = !usedCodes.has(code);
    if (request.requireUsed && isNewCode) {
      throw new StickerError(
        'collection_conflict',
        'Scan a robot that already belongs to the existing collection.'
      );
    }

    const key = linkGroupKey(code);
    if (!codeToKey.has(code)) codeToKey.set(code, key);
    let save = saveStore.get(key);
    if (!save) {
      save = seedSave(code);
      saveStore.set(key, save);
    }

    const sessionId = `stub-${++sessionCounter}`;
    const stored: StoredSession = {
      sessionId,
      code,
      linkedCodes: linkedCodesFor(code),
      tag: tagForCode(code),
      save,
      isNewCode,
    };
    sessionStore.set(sessionId, stored);

    // A scanned code has established/retrieved its save, so it is `used` —
    // matching the first-party server response contract.
    const status: StickerCodeStatus = isNewCode ? 'activated' : 'used';

    return {
      sessionId,
      code,
      saveGameId: saveIdForKey(key),
      status,
      tag: { ...stored.tag },
      linkedCodes: [...stored.linkedCodes],
      members: membersFor(code),
      save: deepCloneJson(save),
      isNewCode,
    };
  }

  async readSave(sessionId: string): Promise<JsonObject> {
    const stored = this.require(sessionId);
    return deepCloneJson(stored.save);
  }

  async writeSave(sessionId: string, data: JsonObject): Promise<void> {
    const stored = this.require(sessionId);
    const cloned = deepCloneJson(data);
    stored.save = cloned;
    // Propagate to the shared link-group save so linked codes stay in sync.
    saveStore.set(linkGroupKey(stored.code), cloned);
  }

  async getLinkedCodes(sessionId: string): Promise<StickerCode[]> {
    const stored = this.require(sessionId);
    return [...stored.linkedCodes];
  }

  async commitNewCollection(sessionId: string): Promise<AdapterScanSession> {
    const stored = this.require(sessionId);
    usedCodes.add(stored.code);
    stored.isNewCode = false;
    return this.snapshot(stored);
  }

  async linkNewCodeToExistingCode(
    newSessionId: string,
    existingSessionId: string
  ): Promise<AdapterScanSession> {
    const source = this.require(newSessionId);
    const target = this.require(existingSessionId);
    const sourceKey = linkGroupKey(source.code);
    const targetKey = linkGroupKey(target.code);

    if (!target.isNewCode && sourceKey === targetKey) {
      source.isNewCode = false;
      return this.snapshot(target);
    }
    if (!source.isNewCode) {
      throw new StickerError(
        'collection_conflict',
        'This robot already belongs to a different collection.'
      );
    }
    if (target.isNewCode) {
      throw new StickerError(
        'collection_conflict',
        'Scan a robot that already belongs to the existing collection.'
      );
    }

    codeToKey.set(source.code, targetKey);
    usedCodes.add(source.code);
    source.isNewCode = false;
    source.save = target.save;
    source.linkedCodes = linkedCodesFor(source.code);

    if (
      sourceKey !== targetKey &&
      ![...codeToKey.values()].some((key) => key === sourceKey)
    ) {
      saveStore.delete(sourceKey);
    }
    return this.snapshot(target);
  }

  async endSession(sessionId: string): Promise<void> {
    sessionStore.delete(sessionId);
  }

  demoFixtures(): StickerCode[] {
    return [...FIXTURE_CODES];
  }

  private require(sessionId: string): StoredSession {
    const stored = sessionStore.get(sessionId);
    if (!stored) {
      throw new StickerError('not_scanning', `Unknown session: ${sessionId}`);
    }
    return stored;
  }

  private snapshot(stored: StoredSession): AdapterScanSession {
    const key = linkGroupKey(stored.code);
    const save = saveStore.get(key) ?? stored.save;
    stored.save = save;
    stored.linkedCodes = linkedCodesFor(stored.code);
    return {
      sessionId: stored.sessionId,
      code: stored.code,
      saveGameId: saveIdForKey(key),
      status: stored.isNewCode ? 'activated' : 'used',
      tag: { ...stored.tag },
      linkedCodes: [...stored.linkedCodes],
      members: membersFor(stored.code),
      save: deepCloneJson(save),
      isNewCode: stored.isNewCode,
    };
  }
}
